import LockIcon from '@mui/icons-material/Lock';
import SendIcon from '@mui/icons-material/Send';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  Authenticated,
  CheckboxGroupInput,
  FileField,
  FileInput,
  SelectInput,
  SimpleForm,
  usePermissions,
} from 'react-admin';
import { useBillingExportStatus } from '../../hooks/useBillingExportStatus';
import { ChoicesType } from '../../interfaces';
import { BillingTransform, ExtensionType, IBillingClinic } from '../../interfaces/billing';
import { AsyncExportStatusResponse } from '../../interfaces/billing.async';
import { AlertComponentProps, SnackBarProps } from '../../interfaces/common';
import { getClinicsForSelect } from '../../lib/clinic';
import { AuthService } from '../../services';
import { BillingService } from '../../services/billing';
import { Roles, billingExport, labelButton } from '../../util/constants';
import { downloadFileFromBase64 } from '../../util/file';
import { CustomAlertComponent } from '../common/CustomAlert';
import { CustomSnackbar } from '../common/CustomSnackbar';
import { SectionTitle } from '../common/SectionTitle';
import { AsyncExportProgress } from './AsyncExportProgress';

export const BillingExporter: FC = () => {
  const { loaded } = usePermissions();
  const [selectClinic, setSelectedClinic] = useState<IBillingClinic>();
  const [selectedBillingExport, setSelectedBillingExport] = useState<File>();
  const [selectedExportTypes, setSelectedExportTypes] = useState<string[]>();
  const [snackbarProps, setSnackbarProps] = useState<SnackBarProps>();
  const [processingBillingExport, setProcessingBillingExport] = useState<boolean>(false);
  const [clinics, setClinics] = useState<ChoicesType[]>([]);

  // Async mode state (enabled by default for better UX with large files)
  const [useAsyncMode, setUseAsyncMode] = useState<boolean>(true);
  const [asyncRequestId, setAsyncRequestId] = useState<string | null>(null);
  const [asyncExportResult, setAsyncExportResult] = useState<AsyncExportStatusResponse | null>(
    null,
  );

  // Async export status polling hook
  const {
    status: asyncStatus,
    isLoading: asyncIsLoading,
    isPolling: asyncIsPolling,
    error: asyncError,
  } = useBillingExportStatus({
    requestId: asyncRequestId,
    autoStart: true,
    onComplete: (completedStatus) => {
      setAsyncExportResult(completedStatus);
      setProcessingBillingExport(false);
    },
    onError: () => {
      setProcessingBillingExport(false);
      // No snackbar needed - AsyncExportProgress shows the error
    },
  });

  const getAlerts: AlertComponentProps[] = [
    {
      key: 'alert-1',
      title: 'What is the flow?',
      content: <b>{billingExport.FLOW}</b>,
      severity: 'info',
    },
    {
      key: 'alert-2',
      title: 'Documentation',
      content: (
        <Link href={billingExport.URL_DOCUMENTATION} target='_blank'>
          {billingExport.URL_DOCUMENTATION}
        </Link>
      ),
      severity: 'info',
    },
    {
      key: 'alert-3',
      title: 'Warning',
      content: <p>{billingExport.FEATURE}</p>,
      severity: 'warning',
    },
  ];

  // Download handlers for async mode
  const handleDownloadAsyncOutput = useCallback(() => {
    const result = asyncExportResult || asyncStatus;
    if (!result?.result) return;

    const isHL7 = selectedExportTypes?.includes('hl7') ?? false;

    if (result.result.outputFileUrl) {
      window.open(result.result.outputFileUrl, '_blank');
    } else if (result.result.outputFileContent) {
      const extension = isHL7 ? ExtensionType.zip : ExtensionType.csv;
      downloadFileFromBase64(result.result.outputFileContent, `export_${Date.now()}.${extension}`);
    }
  }, [asyncExportResult, asyncStatus, selectedExportTypes]);

  const handleDownloadAsyncErrors = useCallback(() => {
    const result = asyncExportResult || asyncStatus;
    if (!result?.result) return;

    if (result.result.errorFileUrl) {
      window.open(result.result.errorFileUrl, '_blank');
    } else if (result.result.errorFileContent) {
      downloadFileFromBase64(result.result.errorFileContent, `export_errors_${Date.now()}.csv`);
    }
  }, [asyncExportResult, asyncStatus]);

  // Reset handler to start a new export
  const handleResetExport = useCallback(() => {
    setAsyncRequestId(null);
    setAsyncExportResult(null);
    setProcessingBillingExport(false);
    setSelectedClinic(undefined);
    setSelectedBillingExport(undefined);
  }, []);

  // Check if we're in an active export state (processing or has results)
  const isExportActive = asyncRequestId !== null;

  const ContentForm = () =>
    clinics.length ? (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SelectInput source='clinic' choices={clinics} isRequired onChange={setSelectedClinic} />
        </Grid>
        <Grid item xs={12} md={6}>
          <CheckboxGroupInput
            source='Export Type'
            choices={[{ id: 'hl7', name: 'HL7' }]}
            defaultChecked={true}
            onChange={handleChangeExportType}
            row={true}
          />
        </Grid>
        <Grid item xs={12}>
          <FileInput
            source={'files'}
            accept={'text/csv'}
            label={'File'}
            isRequired
            multiple={false}
            placeholder={<div>{billingExport.FILE_INPUT}</div>}
            onChange={setSelectedBillingExport}>
            <FileField source={'src'} title={'title'} />
          </FileInput>
        </Grid>
      </Grid>
    ) : (
      <CircularProgress />
    );

  const handleChangeExportType = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSelectedExportTypes(event as any as string[]);

  const downloadAllFiles = async (files: BillingTransform[] | null, isHL7: boolean) => {
    if (!files) {
      return;
    }

    for (const fileItem in files) {
      const file = files[fileItem] as BillingTransform;

      if (!file.success) {
        continue;
      }

      const successFile = file.data?.file;
      const errorFile = file.data?.errors;
      const version = fileItem + 1;

      if (successFile) {
        console.info(`Downloading file ${version}...`);
        const extension = isHL7 ? ExtensionType.zip : ExtensionType.csv;
        downloadFileFromBase64(successFile, `${new Date().valueOf()}_v${version}.${extension}`);
      }

      if (errorFile) {
        console.info(`Downloading error file ${version}...`);
        downloadFileFromBase64(
          errorFile,
          `${new Date().valueOf()}_v${version}.${ExtensionType.csv}`,
        );
      }
    }

    setSnackbarProps({
      open: true,
      message: 'Downloading file(s)',
      severity: 'success',
      horizontal: 'center',
      vertical: 'top',
      onClose: () => {
        setSnackbarProps({ open: false });
      },
    });
  };

  const handleOnUploadCsv = async (): Promise<void> => {
    if (!selectClinic) {
      return setSnackbarProps({
        open: true,
        message: 'Please select a clinic',
        severity: 'error',
        horizontal: 'center',
        vertical: 'top',
        onClose: () => {
          setSnackbarProps({ open: false });
        },
      });
    }

    if (!selectedBillingExport) {
      return setSnackbarProps({
        open: true,
        message: 'Please select a file',
        severity: 'error',
        horizontal: 'center',
        vertical: 'top',
        onClose: () => {
          setSnackbarProps({ open: false });
        },
      });
    }

    setProcessingBillingExport(true);
    setSelectedBillingExport(selectedBillingExport);

    const formData: FormData = new FormData();
    formData.append('file', selectedBillingExport);

    const clinicId = Number(selectClinic.target?.value);
    const isHL7: boolean = selectedExportTypes?.includes('hl7') ?? false;
    const billingService = new BillingService();

    if (useAsyncMode) {
      // Async mode: start export and poll for status
      setAsyncRequestId(null);
      setAsyncExportResult(null);

      const asyncResponse = await billingService.startAsyncExport(formData, clinicId, isHL7);

      if (!asyncResponse) {
        setProcessingBillingExport(false);
        return setSnackbarProps({
          open: true,
          message: 'Failed to start async export',
          severity: 'error',
          horizontal: 'center',
          vertical: 'top',
          onClose: () => setSnackbarProps({ open: false }),
        });
      }

      // Set the request ID to trigger polling - this will show AsyncExportProgress
      setAsyncRequestId(asyncResponse.requestId);
    } else {
      // Sync mode: original behavior
      const billingResponse = await billingService.getTransformedFile(formData, clinicId, isHL7);

      setProcessingBillingExport(false);
      setSelectedClinic({ target: { value: '' } });
      setSelectedBillingExport(new File([], 'No file Selected'));

      if (!billingResponse) {
        setSnackbarProps({
          open: true,
          message: 'Something went wrong',
          severity: 'error',
          horizontal: 'center',
          vertical: 'top',
          onClose: () => {
            setSnackbarProps({ open: false });
          },
        });
      }

      await downloadAllFiles(billingResponse, isHL7);

      window.location.reload();
    }
  };

  useEffect(() => {
    (async () => {
      const ids: number[] = JSON.parse(`${process.env.REACT_APP_DRCHRONO_ALLOWED_CLINIC_IDS}`);
      const clinicsAllowed = await getClinicsForSelect({
        ids,
      });
      setClinics(clinicsAllowed);
    })();
  }, []);

  return (
    <>
      {loaded ? (
        <>
          {AuthService.checkRoleAccess(Roles.Billing) ? (
            <>
              <Authenticated>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Card>
                      <CardContent>
                        <SectionTitle
                          label={billingExport.TITLE_PAGE}
                          variant='h5'
                          style={{ fontWeight: '500' }}
                        />

                        <Divider />

                        {/* Show form only when not in active export */}
                        {!isExportActive ? (
                          <>
                            <SimpleForm toolbar={false}>
                              <ContentForm />
                            </SimpleForm>

                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Button
                                variant='contained'
                                disabled={processingBillingExport}
                                onClick={handleOnUploadCsv}
                                startIcon={<SendIcon />}>
                                {labelButton.SEND}
                              </Button>
                            </Box>
                          </>
                        ) : (
                          /* Show progress when export is active */
                          <AsyncExportProgress
                            status={asyncStatus}
                            isLoading={asyncIsLoading}
                            isPolling={asyncIsPolling}
                            error={asyncError}
                            onDownloadOutput={handleDownloadAsyncOutput}
                            onDownloadErrors={handleDownloadAsyncErrors}
                            onReset={handleResetExport}
                          />
                        )}

                        <CustomSnackbar
                          message={snackbarProps?.message}
                          vertical={snackbarProps?.vertical}
                          horizontal={snackbarProps?.horizontal}
                          onClose={snackbarProps?.onClose}
                          severity={snackbarProps?.severity}
                          open={snackbarProps?.open}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardContent>
                        <SectionTitle label='Alerts' variant='h5' style={{ fontWeight: '500' }} />
                        <Divider />
                        {getAlerts.map((alert) => (
                          <CustomAlertComponent
                            key={alert.title}
                            severity={alert.severity}
                            title={alert.title}
                            content={alert.content}
                          />
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Authenticated>
            </>
          ) : (
            <>
              <Card>
                <CardContent>
                  <Container maxWidth='md' className='e-container'>
                    <Grid container spacing={4} justifyContent='center' alignItems='center'>
                      <Grid item xs={12}>
                        <div className='e-center-content'>
                          <LockIcon className='e-icon-custom text-danger' />{' '}
                          {billingExport.NO_ACCESS}
                        </div>
                      </Grid>
                    </Grid>
                  </Container>
                </CardContent>
              </Card>
            </>
          )}
        </>
      ) : (
        <>
          <h1>{billingExport.LOADING}</h1>
        </>
      )}
    </>
  );
};
